import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { default as Input } from 'antd/es/input';
import { useState } from 'react';
import type { ChangeEventHandler } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { Restricted } from 'containers/Restricted';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { Spinner } from '@atoms/Spinner/Spinner';

import { TableLayout } from 'components/ui/templates/TableLayout/TableLayout';

import { BusinessUnitsEmptyState } from './BusinessUnitsEmptyState';
import { BusinessUnitsGrid } from './BusinessUnitsGrid';
import {
  BusinessUnitsListSyncProvider,
  useBusinessUnitsListRefresh,
  useBusinessUnitsListSyncValue
} from './BusinessUnitsListSyncContext';
import {
  BUSINESS_UNITS_BATCH_LOADING_TEST_ID,
  BUSINESS_UNITS_CREATE_LABEL,
  BUSINESS_UNITS_ERROR_COPY,
  BUSINESS_UNITS_ERROR_TEST_ID,
  BUSINESS_UNITS_HEADER_CREATE_TEST_ID,
  BUSINESS_UNITS_LOADING_TEST_ID,
  BUSINESS_UNITS_NO_MATCH_COPY,
  BUSINESS_UNITS_NO_MATCH_TEST_ID,
  BUSINESS_UNITS_PAGE_TEST_ID,
  BUSINESS_UNITS_SCROLL_TEST_ID,
  BUSINESS_UNITS_SEARCH_DEBOUNCE_MS,
  BUSINESS_UNITS_SEARCH_PLACEHOLDER,
  BUSINESS_UNITS_SEARCH_TEST_ID,
  BUSINESS_UNITS_SUBTITLE,
  BUSINESS_UNITS_SUBTITLE_TEST_ID,
  BUSINESS_UNITS_TITLE,
  BUSINESS_UNITS_TITLE_TEST_ID
} from './constants';
import { CreateBusinessUnitModal } from './CreateBusinessUnitModal';
import { useBusinessUnitsList } from './useBusinessUnitsList';

import { EMPTY_STATE_MIN_HEIGHT } from '../constants';
import { DevelopersPageHeader } from '../DevelopersPageHeader';

export const BusinessUnits = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [listVersion, refreshList] = useBusinessUnitsListRefresh();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue(
    '',
    BUSINESS_UNITS_SEARCH_DEBOUNCE_MS
  );

  const {
    businessUnits,
    onScroll,
    isFetching,
    isError,
    updateElement,
    removeElement
  } = useBusinessUnitsList({ search: debouncedSearch, listVersion });

  const syncValue = useBusinessUnitsListSyncValue({
    businessUnits,
    search: debouncedSearch,
    updateElement,
    removeElement,
    refreshList
  });

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearchInput(event.target.value);
    setDebouncedSearch(event.target.value.trim());
  };

  const openCreate = () => setIsCreateOpen(true);
  const closeCreate = () => setIsCreateOpen(false);

  const isListEmpty = businessUnits.length === 0;
  const hasActiveSearch = searchInput !== '' || debouncedSearch !== '';
  const isEmpty = !isFetching && !isError && isListEmpty && !hasActiveSearch;

  const inlineError = (
    <div
      data-testid={BUSINESS_UNITS_ERROR_TEST_ID}
      className="flex items-center justify-center py-12 text-content-secondary"
    >
      {BUSINESS_UNITS_ERROR_COPY}
    </div>
  );

  const renderContent = () => {
    if (isListEmpty && !hasActiveSearch) {
      if (isFetching) {
        return (
          <div
            data-testid={BUSINESS_UNITS_LOADING_TEST_ID}
            className={`flex ${EMPTY_STATE_MIN_HEIGHT} items-center justify-center`}
          >
            <Spinner size="large" />
          </div>
        );
      }

      if (isError) {
        return inlineError;
      }

      return <BusinessUnitsEmptyState onCreate={openCreate} />;
    }

    return (
      <div className="flex flex-col gap-6 pb-6">
        <Input
          data-testid={BUSINESS_UNITS_SEARCH_TEST_ID}
          prefix={
            <MagnifyingGlassIcon className="size-4 text-content-secondary" />
          }
          allowClear
          value={searchInput}
          placeholder={BUSINESS_UNITS_SEARCH_PLACEHOLDER}
          onChange={handleSearchChange}
          className="w-84"
        />
        {!isListEmpty && <BusinessUnitsGrid businessUnits={businessUnits} />}
        {isFetching && (
          <div
            data-testid={BUSINESS_UNITS_BATCH_LOADING_TEST_ID}
            className="flex justify-center py-6"
          >
            <Spinner />
          </div>
        )}
        {isListEmpty &&
          !isFetching &&
          (isError ? (
            inlineError
          ) : (
            <div
              data-testid={BUSINESS_UNITS_NO_MATCH_TEST_ID}
              className="flex items-center justify-center py-12 text-content-secondary"
            >
              {BUSINESS_UNITS_NO_MATCH_COPY}
            </div>
          ))}
      </div>
    );
  };

  return (
    <BusinessUnitsListSyncProvider value={syncValue}>
      <div
        data-testid={BUSINESS_UNITS_SCROLL_TEST_ID}
        className="h-full overflow-y-auto"
        onScroll={onScroll}
      >
        <TableLayout
          containerTestId={BUSINESS_UNITS_PAGE_TEST_ID}
          header={
            <DevelopersPageHeader
              title={BUSINESS_UNITS_TITLE}
              subtitle={BUSINESS_UNITS_SUBTITLE}
              titleTestId={BUSINESS_UNITS_TITLE_TEST_ID}
              subtitleTestId={BUSINESS_UNITS_SUBTITLE_TEST_ID}
              action={
                !isEmpty && (
                  <Restricted requiredPermission="configurations:manage">
                    <PrimaryButton
                      data-testid={BUSINESS_UNITS_HEADER_CREATE_TEST_ID}
                      icon={<PlusIcon className="size-4" />}
                      onClick={openCreate}
                    >
                      {BUSINESS_UNITS_CREATE_LABEL}
                    </PrimaryButton>
                  </Restricted>
                )
              }
            />
          }
          content={renderContent()}
        />
      </div>
      <CreateBusinessUnitModal open={isCreateOpen} onClose={closeCreate} />
    </BusinessUnitsListSyncProvider>
  );
};
